import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Filter,
  User as UserIcon,
  Plus,
  Linkedin,
  Mail,
  FileText,
  RefreshCw,
  Lock,
} from "lucide-react";
import { useDataTable } from "../../hooks/useDataTable";
import { useTableTabs } from "../../hooks/useTableTabs";
import { User } from "../../types";
import ColumnManager from "../ColumnManager";
import TableHeader from "../TableHeader";
import TableRow from "../TableRow";
import TableTabs from "../TableTabs";
import AddFounder from "../AddFounder";
import CSVImport from "../CSVImport";
import EmailPopup from "../EmailPopup";
import { apiService } from "../../services/api";
import {
  useTableKeyboard,
  useUndoRedo,
  useClipboard,
  makeTSV,
  parseTSV,
  normalizeSelection,
  useIntegrationStatus,
} from "./hooks";
import type { Cell, Rect, UndoAction } from "./types";
import { TableContextProvider } from "./contexts";
import { CHECKBOX_WIDTH, NO_COLUMN_WIDTH, DEFAULT_COLUMN_WIDTH, CLEARABLE_BLOCKLIST, colToUpdateKey } from "./constants";
import { LoadingState, ErrorState } from "./components";
import "./DataTable.css";

interface DataTableProps {
  showFilters?: boolean;
  onToggleFilters?: () => void;
  onFounderClick?: (founderId: string) => void;
  onRefreshFounders?: (refreshFn: () => Promise<void>) => void;
  onActiveTabChange?: (activeTab: { id: string; name: string }) => void;
  onShowLinkedInImport?: () => void;
  onShowNotionImport?: (step?: "connect" | "mapping") => void;
  user?: User | null;
  customFieldsRefreshTrigger?: number;
}

const DataTable: React.FC<DataTableProps> = ({
  showFilters = false,
  onToggleFilters,
  onFounderClick,
  onRefreshFounders,
  onActiveTabChange,
  onShowLinkedInImport,
  onShowNotionImport,
  user,
  customFieldsRefreshTrigger,
}) => {
  const navigate = useNavigate();

  const {
    tabs,
    activeTab,
    activeTabId,
    addTab,
    updateActiveTab,
    renameTab,
    deleteTab,
    switchTab,
    addFounderToActiveTab,
    addFounderToTabByName,
    addFounderToTabById,
  } = useTableTabs(user?.id || "default");

  const {
    filteredFounders,
    loading,
    error,
    customFields,
    visibleColumns,
    filters,
    sortState,
    selectedIds,
    handleSort,
    handleFilterChange,
    handleToggleColumn,
    handleToggleSelect,
    handleToggleSelectAll,
    handleFounderUpdate,
    addFounderToList,
    refreshFounders,
    getInitials,
    formatUpdateTime,
    setVisibleColumns: setDataTableVisibleColumns,
    setFilters: setDataTableFilters,
    setSortState: setDataTableSortState,
    setSelectedIds,
    setFounders,
  } = useDataTable(user?.id || "default", user, customFieldsRefreshTrigger);

  // Integration status (Notion/Gmail)
  const {
    notionStatus,
    gmailStatus,
    syncing,
    handleGmailConnect,
    handleNotionSync,
  } = useIntegrationStatus({ refreshFounders });

  // Helper to get founders visible in active tab (reuse the same logic as rendering)
  // NOTE: Founders can appear in multiple tabs via their tabIds array
  // All founders default to having "All People" in their tabIds array
  const getActiveTabFounders = useMemo(() => {
    return filteredFounders.filter((founder) => {
      // Check if founder is explicitly hidden in this tab
      const isNotHidden = !(activeTab.hiddenFounderIds || []).includes(
        founder.id
      );
      if (!isNotHidden) return false;

      // For default tab ("All People"), show all founders (they all have "All People" by default)
      if (activeTab.id === "default" || activeTab.name === "All People") {
        return true;
      }

      // For tabs with names, check if tab name is in founder's tabIds array
      if (activeTab.name) {
        const founderTabIds = founder.tabIds || [];
        const matchesTabIds = founderTabIds.includes(activeTab.name);

        // Also check localStorage founderIds for backward compatibility (manual additions)
        if (activeTab.founderIds !== undefined) {
          const isInArray = activeTab.founderIds.includes(founder.id);
          return matchesTabIds || isInArray;
        }

        return matchesTabIds;
      }

      // Tab without name: use founderIds array only (fallback)
      if (activeTab.founderIds !== undefined) {
        return activeTab.founderIds.includes(founder.id);
      }

      return false;
    });
  }, [filteredFounders, activeTab]);

  // Wrapper for select all that only works on active tab
  const handleToggleSelectAllForActiveTab = useCallback(
    (checked: boolean) => {
      if (checked) {
        // Get only the IDs of founders in the active tab
        const activeTabFounderIds = getActiveTabFounders.map((f) => f.id);
        console.log(
          `Selecting ${activeTabFounderIds.length} founders in active tab:`,
          activeTabFounderIds
        );

        // Create new selection set with only active tab founders
        const newSelectedIds = new Set(activeTabFounderIds);
        setSelectedIds(newSelectedIds);

        // Update founders array - only set selected: true for active tab founders
        setFounders((prev) =>
          prev.map((f) => ({
            ...f,
            selected: newSelectedIds.has(f.id),
          }))
        );
      } else {
        // Deselect all
        console.log("Deselecting all founders");
        setSelectedIds(new Set());
        setFounders((prev) => prev.map((f) => ({ ...f, selected: false })));
      }
    },
    [getActiveTabFounders, setSelectedIds, setFounders]
  );

  // Handler to delete founder from database
  const handleDeleteFounder = useCallback(
    async (founderId: string) => {
      try {
        await apiService.deleteFounder(founderId);
        // Refresh the founders list to remove the deleted founder
        await refreshFounders();
      } catch (error) {
        console.error("Error deleting founder:", error);
        alert("Failed to delete founder. Please try again.");
      }
    },
    [refreshFounders]
  );

  const [showFiltersBar, setShowFiltersBar] = useState<boolean>(false); // Dropdown closed by default
  const [isTableLocked, setIsTableLocked] = useState<boolean>(false); // Lock state for table editing
  const [newRows, setNewRows] = useState<Array<{ id: string; data: any }>>([]);
  const previousSortStateRef = useRef<{
    field: string | null;
    direction: "asc" | "desc" | null;
  } | null>(null);

  // Add state for triggering edit
  const [shouldStartEditing, setShouldStartEditing] = useState<{
    row: number;
    colKey: string;
    initialChar?: string;
  } | null>(null);

  // Email popup state
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [emailPopupData, setEmailPopupData] = useState<{
    emails: any[];
    loading: boolean;
    error: string | null;
    notConnected: boolean;
  }>({
    emails: [],
    loading: false,
    error: null,
    notConnected: false,
  });

  // Handler to add new empty row
  const handleAddNewRow = useCallback(() => {
    // Calculate the maximum originalIndex from displayed rows to avoid conflicts
    // This ensures new rows get indices that don't conflict with existing row indices
    const displayedRowsWithIndices = filteredFounders
      .map((founder, originalIndex) => {
        // Check if this founder should be shown in the current tab (same logic as render)
        let shouldShow = false;
        if (activeTab.name && activeTab.id !== "default") {
          const matchesTabId = founder.tabId === activeTab.name;
          const isNotHidden = !(activeTab.hiddenFounderIds || []).includes(
            founder.id
          );
          if (activeTab.founderIds !== undefined) {
            const isInArray = activeTab.founderIds.includes(founder.id);
            shouldShow = (matchesTabId || isInArray) && isNotHidden;
          } else {
            shouldShow = matchesTabId && isNotHidden;
          }
        } else if (activeTab.id === "default") {
          shouldShow = !(activeTab.hiddenFounderIds || []).includes(founder.id);
        } else {
          if (activeTab.founderIds !== undefined) {
            shouldShow = activeTab.founderIds.includes(founder.id);
          } else {
            shouldShow = false;
          }
        }
        return shouldShow ? originalIndex : -1;
      })
      .filter((idx) => idx !== -1);

    const maxOriginalIndex =
      displayedRowsWithIndices.length > 0
        ? Math.max(...displayedRowsWithIndices)
        : -1;

    // New rows should start after the maximum originalIndex
    // Use filteredFounders.length as a safe starting point (guaranteed to be >= maxOriginalIndex + 1)
    const baseRowIndex = Math.max(
      filteredFounders.length,
      maxOriginalIndex + 1
    );

    // Only pass tabId if user is in a specific tab (not "All People" or default)
    const tabId =
      activeTab?.name &&
      activeTab.name !== "All People" &&
      activeTab.id !== "default"
        ? activeTab.name
        : undefined;

    const newRowId = `new-${Date.now()}-${Math.random()
      .toString(36)
      .substr(2, 9)}`;
    const newRowData = {
      id: newRowId,
      name: "",
      email: "",
      company: "",
      companyInitial: "",
      companyColor: "#10b981",
      stage: "",
      teamMember: "System",
      roleTitle: "",
      profileUrl: "",
      industry: "",
      location: "",
      tabId: tabId,
    };

    // Add new row to the array
    setNewRows((prev) => {
      const updated = [...prev, { id: newRowId, data: newRowData }];
      // Auto-trigger editing on the name field after row renders
      // Calculate rowIndex to match what will be rendered
      setTimeout(() => {
        const newRowArrayIndex = updated.length - 1; // Index in the newRows array (0-based)
        // In render, new rows use: displayFoundersCount + newRowArrayIndex
        // But to avoid conflicts, we'll use a consistent calculation
        // Recalculate displayFoundersCount at render time to match exactly
        const newRowIndex = baseRowIndex + newRowArrayIndex;
        setShouldStartEditing({ row: newRowIndex, colKey: "name" });
      }, 100);
      return updated;
    });
  }, [activeTab, filteredFounders]);

  // Handler to auto-save new row when name is entered or any field is updated
  const handleNewRowUpdate = useCallback(
    async (founderId: string, updates: any) => {
      // Find the new row being updated
      const newRowIndex = newRows.findIndex((row) => row.id === founderId);
      if (newRowIndex === -1) return;

      const currentRow = newRows[newRowIndex];
      const updatedData = { ...currentRow.data, ...updates };

      // Update the row data
      setNewRows((prev) =>
        prev.map((row, idx) =>
          idx === newRowIndex ? { ...row, data: updatedData } : row
        )
      );

      // Check if name exists and if this row hasn't been saved yet
      const hasName = updatedData.name?.trim();
      const isNotSaved = !currentRow.data._savedToDatabase;

      // Auto-save when name exists and row hasn't been saved yet
      // Only save once per row - after that, it's in the database
      if (hasName && isNotSaved) {
        try {
          // Store current sort state to restore it later (only on first save)
          if (isNotSaved && (sortState.field || sortState.direction)) {
            previousSortStateRef.current = {
              field: sortState.field,
              direction: sortState.direction,
            };
            // Clear sort to keep new item at bottom
            setDataTableSortState({ field: null, direction: null });
          }

          // Only pass tabId if user is in a specific tab (not "All People" or default)
          // If no tabId is passed, founder will only be added to "All People" tab
          const tabId =
            activeTab?.name &&
            activeTab.name !== "All People" &&
            activeTab.id !== "default"
              ? activeTab.name
              : undefined;

          const founderData: Record<string, any> = {
            name: updatedData.name.trim(),
            email: updatedData.email?.trim() || undefined,
            company: updatedData.company?.trim() || undefined,
            roleTitle: updatedData.roleTitle?.trim() || undefined,
            profileUrl: updatedData.profileUrl?.trim() || undefined,
            industry: updatedData.industry?.trim() || undefined,
            location: updatedData.location?.trim() || undefined,
            stage: updatedData.stage?.trim() || undefined,
            tabId: tabId,
          };

          const newFounder = await apiService.createFoundername(founderData);

          if (newFounder?.id) {
            // Add to active tab
            addFounderToActiveTab(newFounder.id);

            // Add directly to the founders list without refreshing
            addFounderToList(newFounder);

            // Remove this new row from the array (it's now in the database)
            setNewRows((prev) => prev.filter((row) => row.id !== founderId));

            // Don't restore sort automatically - keep new items at bottom
            // User can manually re-sort if they want
            previousSortStateRef.current = null;
          }
        } catch (error: any) {
          console.error("Error creating founder:", error);
          alert(error?.message || "Failed to add person. Please try again.");
          // Restore sort state on error
          if (previousSortStateRef.current) {
            if (previousSortStateRef.current.field) {
              setDataTableSortState({
                field: previousSortStateRef.current.field as any,
                direction: previousSortStateRef.current.direction || "asc",
              });
            }
            previousSortStateRef.current = null;
          }
        }
      }
    },
    [
      newRows,
      activeTab,
      addFounderToActiveTab,
      addFounderToList,
      sortState,
      setDataTableSortState,
    ]
  );
  const isSyncingFromTabRef = React.useRef(false);
  const isInitializingTabRef = React.useRef(false);
  const lastSavedFiltersRef = React.useRef<Record<string, string>>({});
  const lastSavedColumnsRef = React.useRef<string[]>([]);
  const lastSavedSortStateRef = React.useRef<any>({
    field: null,
    direction: null,
  });
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Wrapped renameTab that updates tabId in database
  const handleRenameTab = async (tabId: string, newName: string) => {
    // Find the tab being renamed
    const tabBeingRenamed = tabs.find((tab) => tab.id === tabId);

    if (
      !tabBeingRenamed ||
      !tabBeingRenamed.founderIds ||
      tabBeingRenamed.founderIds.length === 0
    ) {
      // No founders to update, just rename the tab
      renameTab(tabId, newName);
      return;
    }

    // Get founders that belong to this tab
    const founderIdsToUpdate = tabBeingRenamed.founderIds;

    try {
      // Bulk update all founders in this tab to have the new tab name
      await apiService.bulkUpdateFounders(founderIdsToUpdate, {
        tabId: newName,
      });

      // Now update the local tab name
      renameTab(tabId, newName);

      // Refresh founders to get the updated data
      await refreshFounders();
    } catch (error) {
      console.error("Error updating tab name in database:", error);
      // Still rename the tab locally even if database update fails
      renameTab(tabId, newName);
    }
  };

  // Google Sheets-style selection system
  // Preserve the EXACT order from visibleColumns (which comes from Notion)
  // Only ensure 'no' is first and 'update' is last
  const colOrder = useMemo(() => {
    const order: string[] = [];

    // Add 'no' first if it exists
    if (visibleColumns.includes("no")) {
      order.push("no");
    }

    // Add all other columns in their EXACT order from visibleColumns (excluding 'no' and 'update')
    // This preserves the Notion order
    visibleColumns.forEach((col) => {
      if (col !== "no" && col !== "update") {
        order.push(col);
      }
    });

    // Add 'update' last if it exists
    if (visibleColumns.includes("update")) {
      order.push("update");
    }

    return order;
  }, [visibleColumns]);
  const [activeCell, setActiveCell] = useState<{
    row: number;
    colKey: string;
  } | null>(null);
  const [anchorCell, setAnchorCell] = useState<{
    row: number;
    colKey: string;
  } | null>(null);
  const [selectionRect, setSelectionRect] = useState<Rect | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const justFinishedDragRef = useRef(false);

  // Undo system (using extracted hook)
  const { addUndoAction, performUndo, canUndo } = useUndoRedo({
    handleFounderUpdate,
    filteredFounders,
  });

  // Helper to get cell value
  const getVal = useCallback(
    (r: number, k: string) => {
      const founder = filteredFounders[r];
      if (!founder) return "";
      // Map column keys to founder properties
      const fieldMap: Record<string, keyof typeof founder> = {
        email: "email",
        company: "company",
        position: "roleTitle" as any,
        linkedinUrl: "profileUrl" as any,
        industry: "industry",
        location: "location",
        stage: "stage",
        status: "status",
        tag: "tag" as any,
      };
      const field = fieldMap[k] || (k as keyof typeof founder);
      const value = founder[field];
      if (value === null || value === undefined) return "";
      if (Array.isArray(value)) return value.length > 0 ? String(value[0]) : "";
      return String(value);
    },
    [filteredFounders]
  );

  // Clipboard operations (using extracted hook)
  const { copy, paste } = useClipboard({
    activeCell,
    selectionRect,
    colOrder,
    filteredFounders,
    customFields,
    getVal,
    handleFounderUpdate,
    addUndoAction,
  });

  // Clear cell(s) handlers
  const isCustomCol = useCallback(
    (colKey: string) => {
      return (
        !colToUpdateKey[colKey] &&
        !CLEARABLE_BLOCKLIST.has(colKey) &&
        !["followerCount", "priorityScore", "lastTouchAt"].includes(colKey)
      );
    },
    []
  );

  const clearOne = useCallback(
    async (row: number, colKey: string) => {
      if (CLEARABLE_BLOCKLIST.has(colKey)) return;
      const founder = filteredFounders[row];
      if (!founder) return;

      // Get old value for undo
      const oldValue = getVal(row, colKey);

      if (isCustomCol(colKey)) {
        // Custom field: set empty string
        try {
          await apiService.setCustomFieldValue(colKey, founder.id, "");

          // Add undo action
          addUndoAction({
            type: "clear",
            founderId: founder.id,
            colKey,
            oldValue,
            newValue: "",
            isCustomField: true,
          });

          handleFounderUpdate(founder.id, {}); // no built-in update
          refreshFounders();
        } catch (error) {
          console.error("Error clearing custom field:", error);
        }
        return;
      }

      const key = colToUpdateKey[colKey];
      if (!key) return;

      // Build payload
      const payload: any = {};
      if (key === "roleTitle") {
        payload.roleTitle = "";
      } else if (key === "profileUrl") {
        payload.profileUrl = "";
      } else {
        payload[key] = "";
      }

      // Add undo action
      addUndoAction({
        type: "clear",
        founderId: founder.id,
        colKey,
        oldValue,
        newValue: "",
        isCustomField: false,
      });

      // Optimistic UI
      handleFounderUpdate(founder.id, payload);
      try {
        await apiService.updateFounder(founder.id, payload);
      } catch (error) {
        console.error("Error clearing cell:", error);
        refreshFounders();
      }
    },
    [
      filteredFounders,
      handleFounderUpdate,
      refreshFounders,
      isCustomCol,
      getVal,
      addUndoAction,
    ]
  );

  const clearRange = useCallback(
    async (rect: Rect) => {
      const ops: Promise<any>[] = [];
      for (let r = rect.r0; r <= rect.r1; r++) {
        for (let ci = rect.c0; ci <= rect.c1; ci++) {
          const colKey = rect.colOrder[ci];
          ops.push(clearOne(r, colKey));
        }
      }
      await Promise.allSettled(ops);
    },
    [clearOne]
  );

  // Use extracted keyboard hook
  const { onKeyDown } = useTableKeyboard({
    activeCell,
    anchorCell,
    selectionRect,
    colOrder,
    rowCount: filteredFounders.length,
    setActiveCell,
    setAnchorCell,
    setSelectionRect,
    setShouldStartEditing,
    copy,
    paste,
    performUndo,
  });

  // Custom field update handler for context
  const onCustomFieldUpdate = useCallback(
    async (founderId: string, fieldId: string, value: string) => {
      try {
        await apiService.setCustomFieldValue(fieldId, founderId, value);
        handleFounderUpdate(founderId, {});
        refreshFounders();
      } catch (error) {
        console.error("Error updating custom field:", error);
      }
    },
    [handleFounderUpdate, refreshFounders]
  );

  // Paste event handler - uses clipboardData API which doesn't require permission
  const onPaste = useCallback(
    (e: React.ClipboardEvent) => {
      console.log("[DataTable] onPaste event fired", {
        target: e.target,
        activeCell,
        selectionRect,
      });

      // Don't interfere with paste in input fields
      const target = e.target as HTMLElement;
      const isInInput =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable;

      if (isInInput) {
        console.log("[DataTable] Paste in input field, allowing native paste");
        // Let the browser handle paste natively in input fields
        return;
      }

      // Prevent default paste behavior
      e.preventDefault();

      // Get text from clipboard using the paste event's clipboardData
      // This doesn't require permission like navigator.clipboard does
      const text = e.clipboardData.getData("text/plain");

      console.log(
        "[DataTable] Paste text extracted:",
        text,
        "length:",
        text.length
      );
      console.log("[DataTable] Checking paste conditions:", {
        hasActiveCell: !!activeCell,
        hasSelectionRect: !!selectionRect,
        activeCell,
        selectionRect,
      });

      // Allow pasting even if text is empty (empty string is valid)
      // Check if we have an active cell or selection to paste into
      if (activeCell || selectionRect) {
        console.log(
          "[DataTable] Paste detected via paste event, text:",
          text,
          "length:",
          text.length
        );
        paste(text);
      } else {
        console.warn(
          "[DataTable] Paste event but no active cell or selection:",
          { activeCell, selectionRect }
        );
      }
    },
    [activeCell, selectionRect, paste]
  );

  // Mouse handlers for cell selection
  const onCellClick = useCallback(
    (row: number, colKey: string) => {
      console.log("[DataTable] onCellClick - justFinishedDragRef:", justFinishedDragRef.current);
      // Skip if we just finished a drag selection to preserve multi-cell selection
      if (justFinishedDragRef.current) {
        console.log("[DataTable] onCellClick - SKIPPING due to justFinishedDragRef");
        return;
      }
      console.log("[DataTable] onCellClick:", { row, colKey });
      const cell = { row, colKey };
      setActiveCell(cell);
      setAnchorCell(cell); // Set anchor for future Shift+arrow selections
      const rect = normalizeSelection(cell, cell, colOrder);
      setSelectionRect(rect);
      console.log("[DataTable] Set activeCell:", cell, "rect:", rect);

      // Ensure the wrapper gets focus so keyboard shortcuts work
      setTimeout(() => {
        if (wrapperRef.current) {
          wrapperRef.current.focus();
          console.log("[DataTable] Wrapper focused");
        }
      }, 0);
    },
    [colOrder]
  );

  const onCellMouseDown = useCallback(
    (row: number, colKey: string) => {
      console.log("[DataTable] onCellMouseDown:", { row, colKey });
      // Clear the drag-finished flag when starting a new selection
      justFinishedDragRef.current = false;

      const cell = { row, colKey };
      setActiveCell(cell);
      setAnchorCell(cell); // Set anchor for mouse drag selection
      setIsSelecting(true);
      const rect = normalizeSelection(cell, cell, colOrder);
      setSelectionRect(rect);

      // Kill any existing browser selection
      const sel = window.getSelection?.();
      sel?.removeAllRanges();
    },
    [colOrder]
  );

  const onCellMouseMove = useCallback(
    (row: number, colKey: string) => {
      if (isSelecting && activeCell) {
        console.log("[DataTable] onCellMouseMove:", {
          row,
          colKey,
          isSelecting,
        });
        const cell = { row, colKey };
        const rect = normalizeSelection(activeCell, cell, colOrder);
        setSelectionRect(rect);
      }
    },
    [isSelecting, activeCell, colOrder]
  );

  // Global mouseup to stop selection
  useEffect(() => {
    const handleMouseUp = () => {
      console.log("[DataTable] handleMouseUp - isSelecting:", isSelecting);
      if (isSelecting) {
        // Mark that we just finished a drag selection
        // This prevents onCellClick and handleClickOutside from resetting the selection
        justFinishedDragRef.current = true;
        console.log("[DataTable] Set justFinishedDragRef = true");
      }
      setIsSelecting(false);
    };
    window.addEventListener("mouseup", handleMouseUp);
    return () => window.removeEventListener("mouseup", handleMouseUp);
  }, [isSelecting]);

  // Clear selection when clicking outside the table
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      console.log("[DataTable] handleClickOutside - justFinishedDragRef:", justFinishedDragRef.current);

      // Skip if we just finished a drag selection
      if (justFinishedDragRef.current) {
        console.log("[DataTable] handleClickOutside - SKIPPING due to justFinishedDragRef");
        return;
      }

      const target = e.target as HTMLElement;

      // Don't clear if we're clicking on an input, textarea, or button (like modals, dropdowns, etc.)
      if (
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.tagName === "BUTTON"
      ) {
        return;
      }

      // Check if click is outside the table wrapper
      if (wrapperRef.current && !wrapperRef.current.contains(target)) {
        // Clear selection when clicking outside the table
        console.log("[DataTable] handleClickOutside - clearing (outside wrapper)");
        setActiveCell(null);
        setSelectionRect(null);
        setAnchorCell(null);
      } else if (wrapperRef.current && wrapperRef.current.contains(target)) {
        // Click is inside the table wrapper
        // Check if it's actually on a table cell (td or th)
        const cell = target.closest("td, th");
        // If click is not on a cell (e.g., on empty space in the wrapper), clear selection
        if (!cell) {
          console.log("[DataTable] handleClickOutside - clearing (inside wrapper, not on cell)", target);
          setActiveCell(null);
          setSelectionRect(null);
          setAnchorCell(null);
        }
      }
    };

    // Use regular click (not capture) so cell click handlers run first
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Toggle is-selecting class on wrapper when isSelecting changes
  useEffect(() => {
    if (!wrapperRef.current) return;
    wrapperRef.current.classList.toggle("is-selecting", isSelecting);
  }, [isSelecting]);

  // Ensure wrapper is focused when activeCell changes (for keyboard navigation to work)
  useEffect(() => {
    if (activeCell && wrapperRef.current && !isEditing) {
      // Use setTimeout to ensure any blur events from exiting edit mode complete first
      setTimeout(() => {
        if (
          wrapperRef.current &&
          document.activeElement?.tagName !== "INPUT" &&
          document.activeElement?.tagName !== "TEXTAREA"
        ) {
          wrapperRef.current.focus();
        }
      }, 0);
    }
  }, [activeCell, isEditing]);

  // Global keydown to handle Delete/Backspace and Paste for cells
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // Ignore if typing in an input/textarea/contentEditable
      const t = e.target as HTMLElement;
      if (
        isEditing ||
        t.isContentEditable ||
        /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)
      )
        return;

      // Handle Delete/Backspace
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault(); // Prevent browser back on Backspace
        if (
          selectionRect &&
          (selectionRect.r0 !== selectionRect.r1 ||
            selectionRect.c0 !== selectionRect.c1)
        ) {
          clearRange(selectionRect);
        } else if (activeCell) {
          clearOne(activeCell.row, activeCell.colKey);
        }
      }

      // Handle Paste (global, so it works even if table isn't focused)
      // Note: We don't handle paste here anymore - we use the onPaste event handler
      // which doesn't require clipboard permission
      if ((e.metaKey || e.ctrlKey) && e.key === "v") {
        // Let the paste event handle it (onPaste handler below)
        // We don't preventDefault here to allow the paste event to fire
      }

      // Handle Undo (global, so it works even if table isn't focused)
      if ((e.metaKey || e.ctrlKey) && e.key === "z") {
        e.preventDefault();
        performUndo();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    activeCell,
    selectionRect,
    isEditing,
    clearRange,
    clearOne,
    paste,
    performUndo,
  ]);

  // Clear shouldStartEditing after a brief moment (after TableRow processes it)
  useEffect(() => {
    if (shouldStartEditing) {
      // Reset after the row has processed it
      const timer = setTimeout(() => setShouldStartEditing(null), 100);
      return () => clearTimeout(timer);
    }
  }, [shouldStartEditing]);

  // Auto-focus newest row name field when it's created
  useEffect(() => {
    if (newRows.length > 0) {
      const displayFoundersCount = filteredFounders.filter((founder) => {
        // If tab has a name, ALWAYS use tabId from database (source of truth)
        if (activeTab.name && activeTab.id !== "default") {
          const matchesTabId = founder.tabId === activeTab.name;
          const isNotHidden = !(activeTab.hiddenFounderIds || []).includes(
            founder.id
          );
          if (activeTab.founderIds !== undefined) {
            const isInArray = activeTab.founderIds.includes(founder.id);
            return (matchesTabId || isInArray) && isNotHidden;
          }
          return matchesTabId && isNotHidden;
        } else if (activeTab.id === "default") {
          return !(activeTab.hiddenFounderIds || []).includes(founder.id);
        } else {
          if (activeTab.founderIds !== undefined) {
            return activeTab.founderIds.includes(founder.id);
          }
          return false;
        }
      }).length;

      const newestRowIndex = displayFoundersCount + newRows.length - 1;
      setTimeout(() => {
        setShouldStartEditing({ row: newestRowIndex, colKey: "name" });
        setActiveCell({ row: newestRowIndex, colKey: "name" });
      }, 150);
    }
  }, [newRows.length, filteredFounders, activeTab]);

  // Sync tab configuration with data table state when switching tabs
  useEffect(() => {
    const currentTab = tabs.find((tab) => tab.id === activeTabId);
    if (currentTab) {
      isSyncingFromTabRef.current = true;
      // Mark as initializing if tab has no filters set (new tab or tab with empty filters)
      isInitializingTabRef.current =
        Object.keys(currentTab.filters || {}).length === 0;

      // Update visible columns from active tab
      setDataTableVisibleColumns(currentTab.visibleColumns);

      // Update filters from active tab - convert Record<string, string> to FilterState
      // Tab stores only non-empty filters, so we need to merge with default empty state
      const tabFilters = currentTab.filters || {};

      // Reset all filters first, then apply only the filters that exist in the tab
      const newFilters = {
        name: tabFilters.name || "",
        email: tabFilters.email || "",
        company: tabFilters.company || "",
        linkedinUrl: tabFilters.linkedinUrl || "",
        stage: tabFilters.stage || "",
        industry: tabFilters.industry || "",
        location: tabFilters.location || "",
        status: tabFilters.status || "",
        tag: tabFilters.tag || "",
        teamMember: tabFilters.teamMember || "",
      };
      setDataTableFilters(newFilters);

      // Update sort state from active tab
      setDataTableSortState(currentTab.sortState as any);

      // Track what was loaded from the tab to prevent immediately saving it back
      const loadedFilters: Record<string, string> = {};
      Object.entries(newFilters).forEach(([key, value]) => {
        const trimmedValue = (value || "").trim();
        if (trimmedValue) {
          loadedFilters[key] = trimmedValue;
        }
      });
      lastSavedFiltersRef.current = loadedFilters;
      lastSavedColumnsRef.current = [...currentTab.visibleColumns];
      lastSavedSortStateRef.current = currentTab.sortState || {
        field: null,
        direction: null,
      };

      // Load column widths from active tab
      const tabColumnWidths = currentTab.columnWidths || {};
      setColumnWidths(tabColumnWidths);

      // Reset flag after a short delay to allow state updates to complete
      setTimeout(() => {
        isSyncingFromTabRef.current = false;
        isInitializingTabRef.current = false;
      }, 100);
    }
  }, [
    activeTabId,
    tabs,
    setDataTableVisibleColumns,
    setDataTableFilters,
    setDataTableSortState,
  ]);

  // Save changes to active tab whenever state changes (but not when syncing from tab or initializing)
  useEffect(() => {
    if (
      !isSyncingFromTabRef.current &&
      !isInitializingTabRef.current &&
      tabs.find((tab) => tab.id === activeTabId)
    ) {
      // Convert FilterState to Record<string, string>
      // Only save non-empty filters to keep tabs isolated
      const filtersRecord: Record<string, string> = {};
      Object.entries(filters).forEach(([key, value]) => {
        const trimmedValue = (value || "").trim();
        // Only include filters that have actual values
        if (trimmedValue) {
          filtersRecord[key] = trimmedValue;
        }
      });

      // Only save if something actually changed from what we last saved
      const columnsChanged =
        JSON.stringify(visibleColumns.sort()) !==
        JSON.stringify(lastSavedColumnsRef.current.sort());
      const filtersChanged =
        JSON.stringify(filtersRecord) !==
        JSON.stringify(lastSavedFiltersRef.current);
      const sortChanged =
        JSON.stringify(sortState) !==
        JSON.stringify(lastSavedSortStateRef.current);

      if (columnsChanged || filtersChanged || sortChanged) {
        updateActiveTab({
          visibleColumns,
          filters: filtersRecord,
          sortState: sortState as any,
        });

        // Update last saved refs
        lastSavedFiltersRef.current = { ...filtersRecord };
        lastSavedColumnsRef.current = [...visibleColumns];
        lastSavedSortStateRef.current = sortState
          ? { ...sortState }
          : { field: null, direction: null };
      }
    }
  }, [visibleColumns, filters, sortState, activeTabId, tabs, updateActiveTab]);

  const handleTabSort = (field: any) => {
    handleSort(field);
  };

  const handleTabFilterChange = (
    field: keyof typeof filters,
    value: string
  ) => {
    handleFilterChange(field, value);
  };

  const handleTabToggleColumn = (columnId: string) => {
    handleToggleColumn(columnId);
  };

  const [columnWidths, setColumnWidths] =
    useState<Record<string, number>>({});
  const [, setIsResizing] = useState<string | null>(null);

  // Enhanced refresh function that automatically adds new founders to their correct tab
  const enhancedRefreshFounders = useCallback(async () => {
    await refreshFounders((founderId: string, tabId?: string) => {
      console.log(
        "🔄 Auto-adding new founder to correct tab:",
        founderId,
        "tabId:",
        tabId
      );
      if (tabId) {
        // Add to the tab specified in the database
        addFounderToTabByName(founderId, tabId);
      } else {
        // Fallback to active tab if no tabId specified
        console.log("🔄 No tabId, falling back to active tab");
        addFounderToActiveTab(founderId);
      }
    });
  }, [refreshFounders, addFounderToActiveTab, addFounderToTabByName]);

  // Pass the enhanced refresh function to parent component
  useEffect(() => {
    if (onRefreshFounders) {
      onRefreshFounders(enhancedRefreshFounders);
    }
  }, [onRefreshFounders, enhancedRefreshFounders]);

  // Notify parent about active tab changes
  useEffect(() => {
    if (onActiveTabChange && activeTab) {
      onActiveTabChange({ id: activeTab.id, name: activeTab.name });
    }
  }, [onActiveTabChange, activeTab?.id, activeTab?.name]);

  // Sync founders with matching tabId to the tab's founderIds array
  useEffect(() => {
    // Only sync if the tab uses the new system (has founderIds defined) and is not the default tab
    if (
      activeTab.founderIds !== undefined &&
      activeTab.id !== "default" &&
      activeTab.name
    ) {
      const foundersToAdd = filteredFounders
        .filter(
          (founder) =>
            founder.tabId === activeTab.name &&
            !activeTab.founderIds!.includes(founder.id)
        )
        .map((founder) => founder.id);

      if (foundersToAdd.length > 0) {
        console.log(
          `🔄 Syncing ${foundersToAdd.length} founder(s) with tabId "${activeTab.name}" to tab's founderIds:`,
          foundersToAdd
        );
        // Add each founder to the active tab
        foundersToAdd.forEach((founderId) => {
          addFounderToActiveTab(founderId);
        });
      }
    }
  }, [filteredFounders, activeTab, activeTabId, addFounderToActiveTab]);

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showFiltersBar && !target.closest(".filter-dropdown-container")) {
        setShowFiltersBar(false);
      }
    };

    if (showFiltersBar) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showFiltersBar]);

  const getCustomFieldDefinition = (fieldId: string) => {
    return customFields.find((field) => field.id === fieldId);
  };

  const handleMouseDown = (e: React.MouseEvent, columnKey: string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(columnKey);
    document.body.classList.add("resizing");
    document.body.style.userSelect = "none";

    const startX = e.clientX;
    const startWidth = columnWidths[columnKey] || 200;
    let finalWidth = startWidth;

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      const deltaX = e.clientX - startX;
      const newWidth = Math.max(50, startWidth + deltaX);
      finalWidth = newWidth;
      setColumnWidths((prev) => ({ ...prev, [columnKey]: newWidth }));
    };

    const cleanup = () => {
      setIsResizing(null);
      document.body.classList.remove("resizing");
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", cleanup);
      window.removeEventListener("mouseup", cleanup);
      // Save final column widths to active tab
      const updatedWidths = { ...columnWidths, [columnKey]: finalWidth };
      updateActiveTab({ columnWidths: updatedWidths });
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", cleanup);
    window.addEventListener("mouseup", cleanup);
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} />;

  return (
    <>
      {/* Column controls row: buttons + chips */}
      <div className="column-control-row">
        <div className="column-actions">
          <button
            className="add-person-button"
            onClick={() => {
              if (onShowLinkedInImport) {
                onShowLinkedInImport();
              }
            }}
            title="Import contacts from LinkedIn"
          >
            <Linkedin size={14} />
            <span>Import from LinkedIn</span>
          </button>
          <button
            className="add-person-button"
            onClick={() => {
              if (onShowNotionImport) {
                onShowNotionImport();
              }
            }}
            title="Import from Notion"
          >
            <FileText size={14} />
            <span>Import from Notion</span>
          </button>
          {notionStatus?.connected && notionStatus?.hasMapping && (
            <>
              <button
                className="add-person-button"
                onClick={handleNotionSync}
                disabled={syncing}
                title="Sync from Notion (pull latest changes)"
                style={{
                  backgroundColor: syncing ? "#ccc" : "#fff",
                  color: syncing ? "#666" : "#6b7280",
                  cursor: syncing ? "not-allowed" : "pointer",
                  opacity: syncing ? 0.7 : 1,
                }}
              >
                <RefreshCw size={14} className={syncing ? "spinning" : ""} />
                <span>{syncing ? "Syncing..." : "Sync from Notion"}</span>
              </button>
            </>
          )}
          <button
            className="add-person-button"
            onClick={handleGmailConnect}
            title={
              gmailStatus?.connected
                ? `Gmail connected: ${gmailStatus.email}`
                : "Connect Gmail to read emails via chat"
            }
            style={{
              backgroundColor: gmailStatus?.connected ? "#dcfce7" : "#fff",
              color: gmailStatus?.connected ? "#166534" : "#6b7280",
              borderColor: gmailStatus?.connected ? "#86efac" : undefined,
            }}
          >
            <Mail size={14} />
            <span>
              {gmailStatus?.connected ? "Gmail Connected" : "Connect Gmail"}
            </span>
          </button>
          <button
            className="add-person-button"
            onClick={() => {
              const addFounderButton = document.querySelector(
                ".data-table-add-founder-button"
              ) as HTMLButtonElement;
              if (addFounderButton) {
                addFounderButton.click();
              }
            }}
            title="Add New Person"
          >
            <UserIcon size={14} />
            <span>Add Person</span>
          </button>
          <button
            className="add-column-button"
            onClick={() => {
              const trigger = document.getElementById(
                "column-manager-trigger"
              ) as HTMLButtonElement | null;
              trigger?.click();
            }}
            title="Add or remove columns"
          >
            + Columns
          </button>
          <CSVImport
            onFounderAdded={(founderId) => {
              if (founderId) {
                addFounderToActiveTab(founderId);
                refreshFounders();
              }
            }}
            onImportComplete={(count) => {
              if (count > 0) {
                refreshFounders();
              }
            }}
            activeTab={activeTab}
          />
          <div className="filter-dropdown-container">
            <button
              className="filter-toggle-button"
              onClick={() => setShowFiltersBar((prev) => !prev)}
              title="Show filters"
            >
              <Filter size={14} />
              <span>Filter</span>
            </button>
            {showFiltersBar && (
              <div className="filter-dropdown-menu">
                <div className="filter-dropdown-content">
                  {(
                    [
                      "name",
                      "email",
                      "company",
                      "stage",
                      "industry",
                      "location",
                      "status",
                      "tag",
                    ] as const
                  )
                    .filter((key) => visibleColumns.includes(key))
                    .map((key) => {
                      const labelMap: Record<string, string> = {
                        name: "Name",
                        email: "Email",
                        company: "Company",
                        stage: "Stage",
                        industry: "Industry",
                        location: "Location",
                        status: "Status",
                        tag: "Tag",
                      };
                      return (
                        <div key={key} className="filter-dropdown-item">
                          <label className="filter-label">
                            {labelMap[key] || key}
                          </label>
                          <input
                            type="text"
                            placeholder={`Filter by ${labelMap[key] || key}...`}
                            value={(filters as any)[key] || ""}
                            onChange={(e) =>
                              handleTabFilterChange(key as any, e.target.value)
                            }
                            className="filter-dropdown-input"
                          />
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
          <button
            className={`filter-toggle-button ${isTableLocked ? "locked" : ""}`}
            onClick={() => setIsTableLocked((prev) => !prev)}
            title={
              isTableLocked
                ? "Unlock table (enable direct editing)"
                : "Lock table (chat-only editing)"
            }
            style={{
              backgroundColor: isTableLocked ? "#ef4444" : undefined,
              color: isTableLocked ? "white" : undefined,
            }}
          >
            <Lock size={14} />
            <span>Lock</span>
          </button>
        </div>
      </div>

      <ColumnManager
        visibleColumns={visibleColumns}
        onToggleColumn={handleToggleColumn}
        onRefresh={refreshFounders}
        user={user}
        refreshTrigger={customFieldsRefreshTrigger}
      />
      <div className="data-table-container">
        <TableContextProvider
          founders={filteredFounders as any}
          customFields={customFields}
          colOrder={colOrder}
          onFounderUpdate={handleFounderUpdate}
          onCustomFieldUpdate={onCustomFieldUpdate}
        >
          <div
            ref={wrapperRef}
            className="table-wrapper"
            tabIndex={0}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            onCopy={(e) => {
              console.log("[DataTable] onCopy event fired", {
                target: e.target,
                activeCell,
                selectionRect,
              });
              // Don't prevent default - let the copy function handle it via keyboard shortcut
              // The copy function will use navigator.clipboard.writeText which works for write operations
            }}
          >
            <table
              className="data-table"
              style={
                {
                  // Expose left rail widths as CSS variables for sticky columns
                  ["--checkbox-col-width" as any]: `${CHECKBOX_WIDTH}px`,
                  ["--no-col-width" as any]: visibleColumns.includes("no")
                    ? `${NO_COLUMN_WIDTH}px`
                    : `0px`,
                  // Calculate total table width based on all visible columns
                  width: (() => {
                    let totalWidth = CHECKBOX_WIDTH;
                    if (visibleColumns.includes("no")) {
                      totalWidth += NO_COLUMN_WIDTH;
                    }
                    visibleColumns.forEach((colId) => {
                      if (colId !== "checkbox" && colId !== "no") {
                        totalWidth += columnWidths[colId] || DEFAULT_COLUMN_WIDTH;
                      }
                    });
                    return `${totalWidth}px`;
                  })(),
                } as React.CSSProperties
              }
            >
              <TableHeader
                visibleColumns={visibleColumns}
                columnWidths={columnWidths}
                sortState={sortState}
                customFields={customFields}
                selectedIds={selectedIds}
                onSort={handleTabSort}
                onMouseDown={handleMouseDown}
                onReorderColumns={(newOrder) => {
                  setDataTableVisibleColumns(newOrder);
                  updateActiveTab({ visibleColumns: newOrder });
                }}
                getCustomFieldDefinition={getCustomFieldDefinition}
                onRefreshAll={refreshFounders}
                onToggleSelectAll={handleToggleSelectAllForActiveTab}
                onBulkDelete={async () => {
                  // Delete selected founders from database
                  if (
                    selectedIds.size > 0 &&
                    window.confirm(
                      `Are you sure you want to permanently delete ${
                        selectedIds.size
                      } selected person${
                        selectedIds.size > 1 ? "s" : ""
                      }? This action cannot be undone.`
                    )
                  ) {
                    try {
                      await Promise.all(
                        Array.from(selectedIds).map((id) =>
                          apiService.deleteFounder(id)
                        )
                      );
                      // Refresh the founders list to remove deleted founders
                      await refreshFounders();
                      // Clear selection
                      handleToggleSelectAllForActiveTab(false);
                    } catch (error) {
                      console.error("Error deleting founders:", error);
                      alert(
                        "Failed to delete some founders. Please try again."
                      );
                    }
                  }
                }}
                onToggleColumn={handleTabToggleColumn}
              />
              <tbody>
                {filteredFounders
                  .map((founder, originalIndex) => {
                    // Check if this founder should be shown in the current tab
                    let shouldShow = false;
                    // If tab has a name, ALWAYS use tabId from database (source of truth)
                    if (activeTab.name && activeTab.id !== "default") {
                      const matchesTabId = founder.tabId === activeTab.name;
                      const isNotHidden = !(
                        activeTab.hiddenFounderIds || []
                      ).includes(founder.id);
                      if (activeTab.founderIds !== undefined) {
                        const isInArray = activeTab.founderIds.includes(
                          founder.id
                        );
                        // Show if matches tabId OR is in founderIds array (allows manual additions beyond tabId)
                        shouldShow = (matchesTabId || isInArray) && isNotHidden;
                      } else {
                        shouldShow = matchesTabId && isNotHidden;
                      }

                      // Debug logging for Candace
                      if (founder.name?.includes("Candace")) {
                        console.log("🔍 Candace debug:", {
                          name: founder.name,
                          founderTabId: founder.tabId,
                          activeTabName: activeTab.name,
                          matchesTabId,
                          hiddenFounderIds: activeTab.hiddenFounderIds,
                          isNotHidden,
                          shouldShow,
                        });
                      }
                    } else if (activeTab.id === "default") {
                      // Default tab: show founders not hidden
                      shouldShow = !(activeTab.hiddenFounderIds || []).includes(
                        founder.id
                      );
                    } else {
                      // Tab without name: use founderIds array only
                      if (activeTab.founderIds !== undefined) {
                        shouldShow = activeTab.founderIds.includes(founder.id);
                      } else {
                        shouldShow = false;
                      }
                    }

                    return { founder, originalIndex, shouldShow };
                  })
                  .filter(({ shouldShow }) => shouldShow)
                  .map(({ founder, originalIndex }, displayIndex) => (
                    <TableRow
                      key={founder.id}
                      founder={founder}
                      visibleColumns={visibleColumns}
                      columnWidths={columnWidths}
                      customFields={customFields}
                      onFounderClick={onFounderClick}
                      onRefresh={refreshFounders}
                      onToggleSelect={handleToggleSelect}
                      onFounderUpdate={handleFounderUpdate}
                      getInitials={getInitials}
                      formatUpdateTime={formatUpdateTime}
                      rowNumber={displayIndex + 1}
                      onDeleteFounder={handleDeleteFounder}
                      rowIndex={originalIndex}
                      activeCell={activeCell}
                      selectionRect={selectionRect}
                      onCellClick={onCellClick}
                      onCellMouseDown={onCellMouseDown}
                      onCellMouseMove={onCellMouseMove}
                      shouldStartEditing={shouldStartEditing}
                      setIsEditing={setIsEditing}
                      isTableLocked={isTableLocked}
                    />
                  ))}
                {/* New rows for adding founders */}
                {newRows.map((newRow, newRowArrayIndex) => {
                  // Calculate the maximum originalIndex from displayed rows to ensure no conflicts
                  const displayedRowsWithIndices = filteredFounders
                    .map((founder, originalIndex) => {
                      let shouldShow = false;
                      if (activeTab.name && activeTab.id !== "default") {
                        const matchesTabId = founder.tabId === activeTab.name;
                        const isNotHidden = !(
                          activeTab.hiddenFounderIds || []
                        ).includes(founder.id);
                        if (activeTab.founderIds !== undefined) {
                          const isInArray = activeTab.founderIds.includes(
                            founder.id
                          );
                          shouldShow =
                            (matchesTabId || isInArray) && isNotHidden;
                        } else {
                          shouldShow = matchesTabId && isNotHidden;
                        }
                      } else if (activeTab.id === "default") {
                        shouldShow = !(
                          activeTab.hiddenFounderIds || []
                        ).includes(founder.id);
                      } else {
                        if (activeTab.founderIds !== undefined) {
                          shouldShow = activeTab.founderIds.includes(
                            founder.id
                          );
                        } else {
                          shouldShow = false;
                        }
                      }
                      return shouldShow ? originalIndex : -1;
                    })
                    .filter((idx) => idx !== -1);

                  const maxOriginalIndex =
                    displayedRowsWithIndices.length > 0
                      ? Math.max(...displayedRowsWithIndices)
                      : -1;

                  // Use filteredFounders.length as base to ensure we're beyond all originalIndex values
                  const baseRowIndex = Math.max(
                    filteredFounders.length,
                    maxOriginalIndex + 1
                  );
                  const newRowIndex = baseRowIndex + newRowArrayIndex;

                  // Also calculate displayFoundersCount for rowNumber display
                  const displayFoundersCount = displayedRowsWithIndices.length;

                  return (
                    <TableRow
                      key={newRow.id}
                      founder={newRow.data as any}
                      visibleColumns={visibleColumns}
                      columnWidths={columnWidths}
                      customFields={customFields}
                      onFounderClick={onFounderClick}
                      onRefresh={refreshFounders}
                      onToggleSelect={handleToggleSelect}
                      onFounderUpdate={(founderId, updates) => {
                        handleNewRowUpdate(founderId, updates);
                      }}
                      getInitials={getInitials}
                      formatUpdateTime={formatUpdateTime}
                      rowNumber={displayFoundersCount + newRowArrayIndex + 1}
                      rowIndex={newRowIndex}
                      onDeleteFounder={(founderId) => {
                        // Remove the new row from the array if it hasn't been saved yet
                        setNewRows((prev) =>
                          prev.filter((row) => row.id !== founderId)
                        );
                      }}
                      activeCell={activeCell}
                      selectionRect={selectionRect}
                      onCellClick={onCellClick}
                      onCellMouseDown={onCellMouseDown}
                      onCellMouseMove={onCellMouseMove}
                      shouldStartEditing={shouldStartEditing}
                      setIsEditing={setIsEditing}
                      isTableLocked={isTableLocked}
                    />
                  );
                })}
                {/* Plus icon button to add new row */}
                <tr className="add-row-button-row">
                  <td
                    colSpan={visibleColumns.length + 1}
                    style={{
                      padding: "8px",
                      textAlign: "left",
                      borderTop: "1px solid #e5e7eb",
                    }}
                  >
                    <button
                      onClick={handleAddNewRow}
                      className="add-row-plus-button"
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "8px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#6b7280",
                        fontSize: "20px",
                        borderRadius: "4px",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#f3f4f6";
                        e.currentTarget.style.color = "#3b82f6";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                        e.currentTarget.style.color = "#6b7280";
                      }}
                      title="Add new row"
                    >
                      <Plus size={20} />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </TableContextProvider>

        {/* Table Tabs */}
        <TableTabs
          tabs={tabs}
          activeTabId={activeTabId}
          onAddTab={addTab}
          onSwitchTab={switchTab}
          onRenameTab={handleRenameTab}
          onDeleteTab={deleteTab}
        />

        {/* Hidden AddFounder component for Add Person button */}
        <div style={{ display: "none" }}>
          <AddFounder
            onFounderAdded={async (founderId) => {
              // Add the new founder to the active tab BEFORE refreshing
              // This ensures it's only added to the active tab
              if (founderId) {
                addFounderToActiveTab(founderId);
              }
              // Refresh the founders list
              if (refreshFounders) {
                await refreshFounders();
              }
            }}
            buttonClassName="data-table-add-founder-button"
            activeTab={activeTab}
          />
        </div>

        {/* Email Popup */}
        <EmailPopup
          isOpen={showEmailPopup}
          onClose={() => setShowEmailPopup(false)}
          emails={emailPopupData.emails}
          loading={emailPopupData.loading}
          error={emailPopupData.error}
          notConnected={emailPopupData.notConnected}
          onConnect={() => {
            setShowEmailPopup(false);
            // Use full URL to bypass React Router and hit the backend directly
            const apiUrl =
              process.env.REACT_APP_API_URL || "http://localhost:3001";
            window.location.href = `${apiUrl}/api/outlook/connect`;
          }}
        />
      </div>
    </>
  );
};

export default DataTable;
